import type { UiAction} from '@tyndo/types';
import { apiFetch} from '@tyndo/hooks';
import { viewRegistry} from './ViewRegistry';

class ActionRegistry {
 private actions = new Map<string, UiAction & { moduleId: string}>();

 register(moduleId: string, actions: UiAction[]): void {
 for (const action of actions) {
 this.actions.set(action.actionId, {
 ...action,
 moduleId,
});
}
}

 getAction(actionId: string): (UiAction & { moduleId: string}) | null {
 return this.actions.get(actionId) || null;
}

 executeAction(actionId: string, context?: Record<string, unknown>): void {
 const action = this.getAction(actionId);
 if (!action) {
 console.warn(`Action not found: ${actionId}`);
 return;
}

 switch (action.actionType) {
 case'window':
 // Navigate to view (handled by router/navigation)
 this.handleWindowAction(action, context);
 break;
 case'server':
 // Call server endpoint
 this.handleServerAction(action, context);
 break;
 case'url':
 // Open URL
 this.handleUrlAction(action);
 break;
 default:
 console.warn(`Unknown action type: ${action.actionType}`);
}
}

 private handleWindowAction(action: UiAction & { moduleId: string}, context?: Record<string, unknown>): void {
 if (action.viewType && action.model) {
 // Confirm the target view is actually registered before asking the
 // router to navigate. A manifest can reference a view the module never
 // shipped (typo, renamed export, or a view dropped between releases);
 // dispatching the navigate event for it lands the operator on the
 // shell's "View not found" state with no explanation. Surface a
 // dedicated event the shell can log / toast instead, and skip the
 // navigation so we don't bounce the user to a dead route.
 if (!viewRegistry.has(action.model, action.viewType)) {
 console.warn(
 `Action '${action.actionId}' targets an unregistered view (model='${action.model}', viewType='${action.viewType}'). Navigation suppressed.`,
 );
 window.dispatchEvent(
 new CustomEvent('tyndo:action-view-not-found', {
 detail: {
 actionId: action.actionId,
 moduleId: action.moduleId,
 viewType: action.viewType,
 model: action.model,
 viewId: action.viewId,
 context,
},
})
 );
 return;
}
 // This will be handled by the router
 // Emit an event that the app can listen to for navigation
 window.dispatchEvent(
 new CustomEvent('tyndo:navigate-action', {
 detail: {
 moduleId: action.moduleId,
 viewType: action.viewType,
 model: action.model,
 context,
},
})
 );
}
}

 private handleServerAction(action: UiAction & { moduleId: string}, context?: Record<string, unknown>): void {
 if (action.serverEndpoint) {
 const payload = context || {};
 // Use shared apiFetch so the call carries Authorization + X-Tenant-Id;
 // a raw fetch here would always come back 401 once Keycloak is real
 // and would also bypass tenant scoping in dev-token mode.
 apiFetch(`/actions/${action.actionId}/execute`, {
 method:'POST',
 json: payload,
})
 .then((res) => res.json())
 .then((data) => {
 // Emit result event
 window.dispatchEvent(
 new CustomEvent('tyndo:action-result', {
 detail: { actionId: action.actionId, result: data},
})
 );
})
 .catch((error) => {
 console.error(`Action execution failed: ${action.actionId}`, error);
 window.dispatchEvent(
 new CustomEvent('tyndo:action-error', {
 detail: { actionId: action.actionId, error: error.message},
})
 );
});
}
}

 private handleUrlAction(action: UiAction): void {
 if (action.url) {
 window.open(action.url,'_blank');
}
}

 listActions(moduleId?: string): (UiAction & { moduleId: string})[] {
 const results: (UiAction & { moduleId: string})[] = [];
 for (const [, action] of this.actions.entries()) {
 if (!moduleId || action.moduleId === moduleId) {
 results.push(action);
}
}
 return results;
}

 clear(): void {
 this.actions.clear();
}
}

export const actionRegistry = new ActionRegistry();
